import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import type {
    AddRatioToActKc2StrategyEnum,
    WorkOrderChangePriceData,
    ChapterEnum,
    WorkOrderPriceStatusEnum,
    FilterValueOfWorkOrderChangePriceCodeFilterData,
    WorkOrderChangeSeasonData,
} from 'gql/graphql';
import { WorkOrderChangeStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { badgePropsByStatus } from 'pages/Project/Common/CodeCell';
import { destroyAgGridTableFilters, getCheckedIdsFromObjValues } from 'utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    WorkOrderChangeCodeRenderer,
    WorkOrderChangeHeaderCellWithTooltip,
    WorkOrderChangeMenuRenderer,
    WorkOrderChangeNumericCell,
    WorkOrderChangeStatusRenderer,
} from '../CellRenderers';
import {
    COST_COLUMNS_WIDTH,
    totalCostText,
    totalCostTooltipText,
    unitCostText,
    unitCostTooltipText,
    VOLUME_COLUMNS_WIDTH,
} from '../constants';
import {
    workOrderChangePriceCodeFilterGql,
    workOrderChangePriceNameFilterGql,
    workOrderChangePriceStatusFilterGql,
} from '../WorkOrderChangeQueries';

import { useWorkOrderChangeSeasonsColumns } from './useWorkOrderChangeSeasonsColumns';
import { useWorkOrderChangeTypeWorkColumns } from './useWorkOrderChangeTypeWorkColumns';

const codeGroupHelper = (items: FilterValueOfWorkOrderChangePriceCodeFilterData[]) =>
    items?.reduce(
        (
            acc: {
                keyMap: { [key: string]: boolean };
                itemArr: any[];
            },
            { value },
        ) => {
            if (value?.parentCode) {
                if (!acc.keyMap[value.parentCode]) {
                    acc.keyMap[value.parentCode] = true;

                    acc.itemArr.push({
                        isParent: true,
                        ...value,
                    });

                    if (value?.code !== value?.parentCode) {
                        acc.itemArr.push({
                            isParent: false,
                            ...value,
                        });
                    }
                } else if (value?.code !== value?.parentCode) {
                    acc.itemArr.push({
                        isParent: false,
                        ...value,
                    });
                }
            }

            return acc;
        },
        {
            keyMap: {},
            itemArr: [],
        },
    ).itemArr;
const codeWhereVariablesGetter = (searchStr: string) => ({
    value: {
        or: [
            {
                parentCode: { containsi: searchStr },
            },
            {
                code: { containsi: searchStr },
            },
        ],
    },
});

interface IUseWorkOrderChangeColumns {
    workOrderChangeId: number;
    isActionsDisabled: boolean;
    statusWorkOrderChange: WorkOrderChangeStatusEnum | undefined;
    handleUpdateVolume: (v: any) => void;
    workOrderObjectId: number | undefined;
    workOrderChangeObjectId: number | undefined;
    chapter: ChapterEnum;
    changeFilterHandler: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: { data: any } }) => void;
    gridApiRef: AgGridReact<any>;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
    costRounding: number;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
    refreshTable: () => void;
}

export const useWorkOrderChangeColumns = (props: IUseWorkOrderChangeColumns) => {
    const {
        workOrderChangeId,
        isActionsDisabled,
        statusWorkOrderChange,
        handleUpdateVolume,
        workOrderObjectId,
        workOrderChangeObjectId,
        chapter,
        changeFilterHandler,
        gridApiRef,
        workOrderChangeUerRules,
        costRounding,
        workOrderChangeSeasons,
        refreshTable,
    } = props;

    const filterVariables = useMemo(
        () => ({
            workOrderChangeId,
            workOrderObjectId,
            workOrderChangeObjectId,
            chapter,
        }),
        [chapter, workOrderChangeId, workOrderChangeObjectId, workOrderObjectId],
    );

    const typeWorkColumns = useWorkOrderChangeTypeWorkColumns({
        statusWorkOrderChange,
        onWorkRatioEditSuccess: refreshTable,
    });

    const seasonColumns = useWorkOrderChangeSeasonsColumns({
        workOrderChangeId,
        isActionsDisabled,
        handleUpdateVolume,
        workOrderChangeUerRules,
        costRounding,
        workOrderChangeSeasons,
        workOrderObjectId,
        workOrderChangeObjectId,
        chapter,
        changeFilterHandler,
    });

    const [checkedRows, setCheckedRows] = useState({});
    const [isSingleDeleteModalOpened, setIsSingleDeleteModalOpened] = useState(false);
    const [isAddMtrModalOpened, setIsAddMtrModalOpened] = useState(false);
    const [isEditMtrModalOpened, setIsEditMtrModalOpened] = useState(false);
    const [isEditNoteModalOpened, setIsEditNoteModalOpened] = useState(false);
    const [isEditRatioModalOpened, setIsEditRatioModalOpened] = useState(false);
    const [isEditUerModalOpened, setIsEditUerModalOpened] = useState(false);
    const [rowData, setRowData] = useState();
    const [isAddRatioModalOpenedAndStrategy, setIsAddRatioModalOpenedAndStrategy] = useState<
        boolean | AddRatioToActKc2StrategyEnum
    >(false);
    const [isMoveVolumesBetweenSeasonsForUerOpen, setIsMoveVolumesBetweenSeasonsForUerOpen] = useState(false);
    const [isMatchMtrDialogOpen, setIsMatchMtrDialogOpen] = useState(false);

    const closeMatchMtrDialogHandler = useCallback(() => {
        setIsMatchMtrDialogOpen(false);
        setCheckedRows({});
    }, []);

    const openMatchMtrDialogHandler = useCallback(() => {
        setIsMatchMtrDialogOpen(true);
    }, []);

    const closeSingleDeleteModalHandler = useCallback(() => {
        setIsSingleDeleteModalOpened(false);
        setCheckedRows({});
    }, []);

    const openSingleDeleteModalHandler = useCallback(() => {
        setIsSingleDeleteModalOpened(true);
    }, []);

    const openEditUerModalHandler = useCallback(() => {
        setIsEditUerModalOpened(true);
    }, []);

    const closeEditUerModalHandler = useCallback(() => {
        setIsEditUerModalOpened(false);
        setCheckedRows({});
    }, []);

    const openAddMtrModalHandler = useCallback(() => {
        setIsAddMtrModalOpened(true);
    }, []);

    const closeAddMtrModalHandler = useCallback(() => {
        setIsAddMtrModalOpened(false);
        setCheckedRows({});
    }, []);

    const openEditMtrModalHandler = useCallback(() => {
        setIsEditMtrModalOpened(true);
    }, []);

    const closeEditMtrModalHandler = useCallback(() => {
        setIsEditMtrModalOpened(false);
        setCheckedRows({});
    }, []);

    const openEditNoteModalHandler = useCallback(() => {
        setIsEditNoteModalOpened(true);
    }, []);

    const closeEditNoteModalHandler = useCallback(() => {
        setIsEditNoteModalOpened(false);
    }, []);

    const openAddRatioModalHandler = useCallback((strategy: AddRatioToActKc2StrategyEnum | null) => {
        if (strategy === null) {
            setIsAddRatioModalOpenedAndStrategy(true);
        } else {
            setIsAddRatioModalOpenedAndStrategy(strategy);
        }
    }, []);

    const openMoveVolumesBetweenSeasonsForUer = useCallback(() => {
        setIsMoveVolumesBetweenSeasonsForUerOpen(true);
    }, []);

    const closeAddRatioModalHandler = useCallback(() => {
        setIsAddRatioModalOpenedAndStrategy(false);
        setCheckedRows({});
    }, []);

    const openEditRatioModal = useCallback(() => {
        setIsEditRatioModalOpened(true);
    }, []);

    const closeEditRatioModal = useCallback(() => {
        setIsEditRatioModalOpened(false);
        setCheckedRows({});
    }, []);

    const closeMoveVolumesBetweenSeasonsForUer = useCallback(() => {
        setIsMoveVolumesBetweenSeasonsForUerOpen(false);
        setCheckedRows({});
    }, []);

    const checkedRowsArr = useMemo(
        () => getCheckedIdsFromObjValues(['workOrderPriceId', 'workOrderChangePriceId'])(checkedRows),
        [checkedRows],
    );

    const checkedUerRow = useMemo(
        () => R.pipe(R.pickBy(R.identity), R.values)(checkedRows)[0] as WorkOrderChangePriceData,
        [checkedRows],
    );

    const checkRowHandler = useCallback(({ id, data }) => {
        setCheckedRows((prev) => ({ ...prev, [id]: data }));
    }, []);

    const checkSingleRowHandler = useCallback(({ id, data }) => {
        setCheckedRows({ [id]: data });
    }, []);

    const resetCheckedRows = useCallback(() => {
        setCheckedRows({});
    }, []);

    const columns = useMemo<(ColDef | ColGroupDef)[]>(
        () => [
            {
                headerName: '',
                field: '',
                width: 80,
                cellRenderer: WorkOrderChangeMenuRenderer({
                    checkRowHandler,
                    checkSingleRowHandler,
                    checkedRows,
                    openSingleDeleteModalHandler,
                    openEditUerModalHandler,
                    openAddMtrModalHandler,
                    openAddRatioModalHandler,
                    openEditRatioModal,
                    openEditMtrModalHandler,
                    isActionsDisabled,
                    workOrderChangeUerRules,
                    openEditNoteModalHandler,
                    setRowData,
                    openMoveVolumesBetweenSeasonsForUer,
                    isOpenMoveVolumesBetweenSeasonsForUerDisabled: workOrderChangeSeasons.length <= 1,
                    openMatchMtrDialogHandler,
                }),
                cellClass: 'centeredAgGridCell firstColumn',
                spanHeaderHeight: true,
                pinned: 'left',
                lockPosition: 'left',
            },
            {
                headerName: 'Статус',
                field: 'status',
                width: 105,
                cellRenderer: WorkOrderChangeStatusRenderer,
                cellClass: 'centeredAgGridCell',
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderChangePriceStatusFilter',
                    query: workOrderChangePriceStatusFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Статус',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({ value }: { value: WorkOrderPriceStatusEnum }) =>
                                badgePropsByStatus[value].label,
                        },
                    ],
                    changeFilterHandler,
                },
                tooltipComponent: null,
            },
            {
                headerName: 'Шифр',
                field: 'code',
                cellRenderer: WorkOrderChangeCodeRenderer,
                width: 150,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderChangePriceCodeFilter',
                    query: workOrderChangePriceCodeFilterGql,
                    childFieldAccessor: 'code',
                    columns: [
                        { headerName: '', lockVisible: true, checkboxSelection: true, width: 50 },
                        {
                            headerName: 'Шифр/УЕР',
                            field: 'parentCode',
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({
                                data,
                            }: {
                                data: { isParent: boolean; code: string; parentCode: string };
                            }) => (data?.isParent ? data.parentCode ?? '' : ''),
                        },
                        {
                            headerName: 'МТР/Коэффициент',
                            field: 'code',
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({
                                data,
                            }: {
                                data: { isParent: boolean; code: string; parentCode: string };
                            }) => (!data?.isParent ? data.code ?? '' : ''),
                        },
                    ],
                    changeFilterHandler,
                    groupAndSortItemsHelper: codeGroupHelper,
                    customWhereVariablesGetter: codeWhereVariablesGetter,
                },
            },
            {
                headerName: 'Наименование ',
                field: 'name',
                resizable: true,
                width: 300,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderChangePriceNameFilter',
                    query: workOrderChangePriceNameFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Ед.изм',
                field: 'unit',
                width: 100,
                spanHeaderHeight: true,
            },
            {
                headerName: 'Стоимость единицы в текущем уровне цен, руб. без НДС',
                headerTooltip: '',
                headerComponent: WorkOrderChangeHeaderCellWithTooltip({
                    text: unitCostText,
                    tooltipText: unitCostTooltipText,
                }),
                field: 'unitCost',
                cellRenderer: WorkOrderChangeNumericCell({ key: 'unitCost' }),
                spanHeaderHeight: true,
            },
            {
                headerName: 'План по НЗ',
                spanHeaderHeight: true,
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalPlanVolume',
                        width: VOLUME_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({ key: 'totalPlanVolume', decimal: 3 }),
                        spanHeaderHeight: true,
                    },
                    {
                        headerName: 'Стоимость всего, руб. без НДС',
                        field: 'totalPlanCost',
                        headerComponent: WorkOrderChangeHeaderCellWithTooltip({
                            text: totalCostText,
                            tooltipText: totalCostTooltipText,
                        }),
                        width: COST_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({ key: 'totalPlanCost', decimal: costRounding }),
                        spanHeaderHeight: true,
                    },
                ],
            },
            {
                headerName: 'Выполнено по НЗ',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalCompletedVolume',
                        width: VOLUME_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({ key: 'totalCompletedVolume', decimal: 3 }),
                    },
                    {
                        headerName: 'Стоимость всего, руб. без НДС',
                        field: 'totalCompletedCost',
                        headerComponent: WorkOrderChangeHeaderCellWithTooltip({
                            text: totalCostText,
                            tooltipText: totalCostTooltipText,
                        }),
                        width: COST_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({ key: 'totalCompletedCost', decimal: costRounding }),
                        spanHeaderHeight: true,
                    },
                ],
            },
            {
                headerName: 'Остаток по НЗ',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalRemainderVolume',
                        width: VOLUME_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({ key: 'totalRemainderVolume', decimal: 3 }),
                    },
                    {
                        headerName: 'Стоимость всего, руб. без НДС',
                        field: 'totalRemainderCost',
                        headerComponent: WorkOrderChangeHeaderCellWithTooltip({
                            text: totalCostText,
                            tooltipText: totalCostTooltipText,
                        }),
                        width: COST_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({ key: 'totalRemainderCost', decimal: costRounding }),
                    },
                ],
            },
            ...typeWorkColumns,
            ...seasonColumns,
        ],
        [
            changeFilterHandler,
            checkRowHandler,
            checkSingleRowHandler,
            checkedRows,
            costRounding,
            filterVariables,
            isActionsDisabled,
            openAddMtrModalHandler,
            openAddRatioModalHandler,
            openEditMtrModalHandler,
            openEditNoteModalHandler,
            openEditRatioModal,
            openEditUerModalHandler,
            openMatchMtrDialogHandler,
            openMoveVolumesBetweenSeasonsForUer,
            openSingleDeleteModalHandler,
            seasonColumns,
            typeWorkColumns,
            workOrderChangeSeasons.length,
            workOrderChangeUerRules,
        ],
    );

    const [selection, , , setSelection] = useSelection('workOrderChangePriceTable');
    const [pinnedItems, , , setPinned] = usePin('workOrderChangePriceTable');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 1) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 6) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setPinned,
                setSelection,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    useEffect(() => {
        destroyAgGridTableFilters({
            api: gridApiRef?.api,
            columns: ['code', 'name', 'status'],
        });
    }, [filterVariables, gridApiRef?.api]);

    useEffect(() => {
        if (statusWorkOrderChange !== WorkOrderChangeStatusEnum.OnFormation) {
            setCheckedRows({});
        }
    }, [statusWorkOrderChange]);

    return {
        fullSetColumns,
        checkedRowsArr,
        checkedUerRow,
        resetCheckedRows,
        isSingleDeleteModalOpened,
        closeSingleDeleteModalHandler,
        isAddMtrModalOpened,
        closeAddMtrModalHandler,
        closeEditNoteModalHandler,
        isAddRatioModalOpenedAndStrategy,
        closeAddRatioModalHandler,
        fullSetSettingsColumns,
        handleTableSettingsConfirm,
        isColumnVisible,
        isEditRatioModalOpened,
        closeEditRatioModal,
        isEditMtrModalOpened,
        closeEditMtrModalHandler,
        isEditUerModalOpened,
        closeEditUerModalHandler,
        isEditNoteModalOpened,
        rowData,
        isMoveVolumesBetweenSeasonsForUerOpen,
        closeMoveVolumesBetweenSeasonsForUer,
        isMatchMtrDialogOpen,
        closeMatchMtrDialogHandler,
    };
};
